import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { UnidadeMedidaService } from '../../../../shared/services/apis/produto/unidade-medida.service';
import { UnidadeMedidaRequest } from '../../../../shared/services/requests/produto/unidade-medida.request';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-unidade-medida-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    HeaderComponent,
    LoadingBoxComponent,
    FooterActionComponent,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
  ],
  templateUrl: './unidade-medida-cadastro.component.html',
  styleUrl: './unidade-medida-cadastro.component.css',
})
export class UnidadeMedidaCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public form: FormGroup;
  public isLoading: boolean = false;
  public isSaving: boolean = false;
  public isEdicao: boolean = false;
  public idUnidadeMedida: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private unidadeMedidaService: UnidadeMedidaService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();

    this.route.params.subscribe(params => {
      if (params['idUnidadeMedida']) {
        this.idUnidadeMedida = +params['idUnidadeMedida'];
        this.isEdicao = true;
        this.carregarUnidadeMedida();
      }
    });
  }

  private createForm(): void {
    this.form = this.formBuilder.group({
      descricao: ['', Validators.required],
      abreviacao: ['', Validators.required],
      isFracionada: [false],
    });
  }

  private carregarUnidadeMedida(): void {
    this.isLoading = true;
    this.unidadeMedidaService.getById(this.idUnidadeMedida).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show(
            'Erro',
            res.errorDescription || 'Erro ao carregar unidade de medida.',
            AlertType.error
          );
          this.voltar();
          return;
        }

        if (res.listaUnidadeMedida && res.listaUnidadeMedida.length > 0) {
          const unidade = res.listaUnidadeMedida[0];
          this.form.patchValue({
            descricao: unidade.descricao,
            abreviacao: unidade.abreviacao,
            isFracionada: unidade.isFracionada,
          });
        } else if (res.unidadeMedida) {
          this.form.patchValue({
            descricao: res.unidadeMedida.descricao,
            abreviacao: res.unidadeMedida.abreviacao,
            isFracionada: res.unidadeMedida.isFracionada,
          });
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Ocorreu um erro ao carregar a unidade de medida.', AlertType.error);
        console.error(err);
        this.voltar();
      },
    });
  }

  public salvar(): void {
    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios.', AlertType.warning);
      return;
    }

    this.isSaving = true;
    const request: UnidadeMedidaRequest = this.form.value;

    if (this.isEdicao) {
      this.unidadeMedidaService.update(this.idUnidadeMedida, request).subscribe({
        next: res => this.tratarRetornoSalvar(res),
        error: err => this.tratarErroSalvar(err),
      });
    } else {
      this.unidadeMedidaService.create(request).subscribe({
        next: res => this.tratarRetornoSalvar(res),
        error: err => this.tratarErroSalvar(err),
      });
    }
  }

  private tratarRetornoSalvar(res: any): void {
    this.isSaving = false;
    if (res.isError) {
      const msg =
        res.errorDescription && res.errorDescription.toLowerCase().includes('duplicate')
          ? 'Já existe uma unidade de medida com esta descrição ou abreviação.'
          : res.errorDescription || 'Erro ao salvar unidade de medida.';

      this.alertService.show('Atenção', msg, AlertType.warning);
    } else {
      this.alertService.show('Sucesso', 'Unidade de medida salva com sucesso.', AlertType.success);
      this.voltar();
    }
  }

  private tratarErroSalvar(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
    console.error(err);
  }

  public voltar(): void {
    this.router.navigate(['/configuracoes/cadastros/unidades-medida']);
  }
}
